import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, X, Check } from "lucide-react";
import ConflictWarningDialog from "@/components/ConflictWarningDialog";

/** Phase 36 — Capture / edit a tentative pre-booking enquiry.
 *  Modes:
 *    - mode="create"  → POST /booking-enquiries
 *    - mode="edit"    → PUT  /booking-enquiries/{id}
 *  Props:
 *    open, onOpenChange, onSaved (enquiry) → void
 *    initial (existing enquiry doc, only for edit mode)
 */
export default function EnquiryDialog({ open, onOpenChange, mode = "create", initial = null, initialDate = null, onSaved }) {
  const isEdit = mode === "edit";

  // Customer typeahead state — used only in create mode. In edit mode the
  // enquiry already carries a snapshot of name+phone.
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  // Form state
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    address: "",
    tentative_date: "",
    tentative_time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Phase 48 — soft customer-overlap warning. Enquiries carry no staff
  // or duration, so only the customer-overlap branch of the conflict
  // endpoint can fire. We still gate the call on the settings toggle to
  // skip the round-trip entirely when the manager has disabled it.
  const [warnOnTimeConflicts, setWarnOnTimeConflicts] = useState(true);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const pendingBodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setWarnOnTimeConflicts(data?.warn_on_time_conflicts !== false);
      } catch { /* default true */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Re-seed form when dialog opens.
  useEffect(() => {
    if (!open) return;
    if (isEdit && initial) {
      let date = "", time = "";
      if (initial.tentative_start) {
        const d = new Date(initial.tentative_start);
        if (!isNaN(d.getTime())) {
          date = d.toISOString().slice(0, 10);
          time = d.toTimeString().slice(0, 5);
        }
      }
      setForm({
        customer_name: initial.customer_name || "",
        customer_phone: initial.customer_phone || "",
        address: initial.address || "",
        tentative_date: date,
        tentative_time: time,
        notes: initial.notes || "",
      });
      setSelectedCustomer(initial.customer_id ? { id: initial.customer_id } : null);
    } else {
      // Phase 45 — when opened from the day-detail drawer's "+ Add
      // enquiry" button we pre-fill the tentative_date so the manager
      // can confirm/adjust the time without re-typing the day.
      let seedDate = "";
      if (initialDate instanceof Date && !isNaN(initialDate.getTime())) {
        seedDate = initialDate.toISOString().slice(0, 10);
      }
      setForm({ customer_name: "", customer_phone: "", address: "", tentative_date: seedDate, tentative_time: "", notes: "" });
      setSelectedCustomer(null);
      setCustQuery("");
      setCustResults([]);
      setShowQuickAdd(false);
    }
  }, [open, isEdit, initial, initialDate]);

  // Debounced typeahead.
  const searchCustomers = useCallback(async (q) => {
    if (!q?.trim()) { setCustResults([]); return; }
    try {
      const { data } = await api.get("/customers/search", { params: { phone: q } });
      let extras = [];
      try {
        const { data: d2 } = await api.get("/customers", { params: { q } });
        extras = (d2 || []).filter((c) => !data.some((x) => x.id === c.id));
      } catch { /* swallow */ }
      setCustResults([...data, ...extras].slice(0, 8));
    } catch {
      setCustResults([]);
    }
  }, []);

  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(() => searchCustomers(custQuery), 250);
    return () => clearTimeout(t);
  }, [custQuery, searchCustomers, isEdit]);

  const pickExisting = (c) => {
    setSelectedCustomer(c);
    setForm((f) => ({
      ...f,
      customer_name: c.company_name || c.contact_name || f.customer_name,
      customer_phone: c.phones?.[0] || f.customer_phone,
    }));
    setShowQuickAdd(false);
    setCustQuery("");
    setCustResults([]);
  };

  const clearSelection = () => {
    setSelectedCustomer(null);
    setForm((f) => ({ ...f, customer_name: "", customer_phone: "" }));
  };

  const buildTentativeStartISO = () => {
    if (!form.tentative_date) return null;
    const time = form.tentative_time || "09:00";
    // Local-zone interpretation — convert to ISO with offset.
    const local = new Date(`${form.tentative_date}T${time}:00`);
    if (isNaN(local.getTime())) return null;
    return local.toISOString();
  };

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.customer_phone.trim()) { toast.error("Phone is required"); return; }
    if (!form.notes.trim()) { toast.error("Notes are required"); return; }
    const tentativeIso = buildTentativeStartISO();
    let body;
    if (isEdit) {
      body = {
        customer_phone: form.customer_phone.trim(),
        address: form.address.trim() || null,
        tentative_start: tentativeIso || "",
        notes: form.notes.trim(),
      };
    } else {
      body = {
        customer_id: selectedCustomer?.id || null,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        address: form.address.trim() || null,
        tentative_start: tentativeIso,
        notes: form.notes.trim(),
      };
    }

    // Phase 48 — only customer-overlap branch applies to enquiries (no
    // staff, no duration). Skip entirely if the toggle is off or we
    // can't supply a linked customer + a real tentative start.
    const linkedCustomerId = selectedCustomer?.id || initial?.customer_id || null;
    if (warnOnTimeConflicts && linkedCustomerId && tentativeIso) {
      try {
        const { data: check } = await api.post("/bookings/check-conflicts", {
          customer_id: linkedCustomerId,
          scheduled_start: tentativeIso,
          duration_minutes: 60,
          location: null,
        });
        if (check?.settings_enabled && (check.conflicts || []).length > 0) {
          pendingBodyRef.current = body;
          setConflicts(check.conflicts);
          setConflictOpen(true);
          return;
        }
      } catch { /* non-fatal — fall through to actual save */ }
    }
    await commitEnquiry(body);
  };

  const commitEnquiry = async (body) => {
    setSubmitting(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/booking-enquiries/${initial.id}`, body);
        toast.success("Enquiry updated");
        onSaved && onSaved(data);
      } else {
        const { data } = await api.post("/booking-enquiries", body);
        toast.success("Enquiry saved");
        onSaved && onSaved(data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onConflictProceed = async () => {
    setConflictOpen(false);
    const body = pendingBodyRef.current;
    pendingBodyRef.current = null;
    if (body) await commitEnquiry(body);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit enquiry" : "New enquiry / pencil-in"}</DialogTitle>
          <DialogDescription>
            Capture a phone enquiry that isn{"\u2019"}t a confirmed booking yet. Auto-expires after 30 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Customer picker (create mode only) */}
          {!isEdit && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Customer</Label>
              {selectedCustomer?.id ? (
                <div className="bg-secondary/40 border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid="enquiry-selected-customer">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm">{form.customer_name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{form.customer_phone}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="enquiry-change-customer">
                    <X className="h-3.5 w-3.5 mr-1" /> Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search existing by name or phone…"
                      value={custQuery}
                      onChange={(e) => setCustQuery(e.target.value)}
                      className="pl-9 h-10"
                      data-testid="enquiry-customer-search-input"
                    />
                  </div>
                  {custQuery && custResults.length > 0 && (
                    <div className="border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto" data-testid="enquiry-customer-results">
                      {custResults.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => pickExisting(c)}
                          className="w-full p-2 text-left hover:bg-secondary/60 transition-colors"
                          data-testid={`enquiry-customer-result-${c.id}`}
                        >
                          <div className="font-semibold text-sm">{c.company_name}</div>
                          <div className="text-xs text-muted-foreground">{c.contact_name} · {c.phones?.[0]}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {!showQuickAdd ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowQuickAdd(true)} data-testid="enquiry-quickadd-toggle">
                      <Plus className="h-3 w-3 mr-1" /> Quick-add (name + phone only)
                    </Button>
                  ) : (
                    <div className="bg-card border border-border rounded-md p-3 text-xs text-muted-foreground">
                      Manual entry below — this won{"\u2019"}t create a customer record until the enquiry is confirmed.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Customer name *</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                disabled={isEdit}
                placeholder="e.g. John Smith"
                data-testid="enquiry-customer-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Phone *</Label>
              <Input
                value={form.customer_phone}
                onChange={(e) => setForm((f) => ({ ...f, customer_phone: e.target.value }))}
                placeholder="+353 …"
                data-testid="enquiry-customer-phone-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider">Address (optional)</Label>
            <Textarea
              rows={2}
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Site or delivery address"
              data-testid="enquiry-address-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Tentative date</Label>
              <Input
                type="date"
                value={form.tentative_date}
                onChange={(e) => setForm((f) => ({ ...f, tentative_date: e.target.value }))}
                data-testid="enquiry-tentative-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider">Tentative time</Label>
              <Input
                type="time"
                value={form.tentative_time}
                onChange={(e) => setForm((f) => ({ ...f, tentative_time: e.target.value }))}
                disabled={!form.tentative_date}
                data-testid="enquiry-tentative-time-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider">Notes *</Label>
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="What the caller asked for, special requests, deadlines…"
              data-testid="enquiry-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="enquiry-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="btn-cta" data-testid="enquiry-save-button">
            <Check className="h-4 w-4 mr-1" /> {isEdit ? "Save changes" : "Save enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ConflictWarningDialog
        open={conflictOpen}
        onOpenChange={setConflictOpen}
        conflicts={conflicts}
        onProceed={onConflictProceed}
        onCancel={() => { setConflictOpen(false); pendingBodyRef.current = null; }}
      />
    </Dialog>
  );
}
